
import {
  DECK_GROUPS,
  HIDDEN_NAV_SHORTCUTS,
  type DeckorateNav,
} from "../components/deckorateHome/ribbonGroups";

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);


const NAV_SHORTCUTS: ReadonlyArray<{
  readonly shortcut: string;
  readonly nav: DeckorateNav;
}> = [
  ...DECK_GROUPS.flatMap((group) =>
    group.cards.flatMap((card) =>
      card.nav && card.shortcut
        ? [{ shortcut: card.shortcut, nav: card.nav }]
        : [],
    ),
  ),
  ...HIDDEN_NAV_SHORTCUTS,
];

/** Maps a shortcut label's final key token to its `KeyboardEvent.code`. */
function keyToCode(key: string): string {
  if (key === "space") return "Space";
  if (/^[a-z]$/.test(key)) return `Key${key.toUpperCase()}`;
  return key;
}


export function matchesShortcut(event: KeyboardEvent, label: string): boolean {
  const tokens = label.toLowerCase().split("+").map((t) => t.trim());
  const key = tokens[tokens.length - 1] ?? "";
  if (event.ctrlKey !== tokens.includes("ctrl")) return false;
  if (event.altKey !== tokens.includes("alt")) return false;
  if (event.shiftKey !== tokens.includes("shift")) return false;
  return event.code === keyToCode(key);
}

/** True when focus is in a control where typing must not trigger shortcuts.
 * Exported for Code Mode's accumulator, which needs the same guard so an
 * armed chord can't swallow letters the user is typing into a field (BR-7). */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return EDITABLE_TAGS.has(el.tagName) || el.isContentEditable;
}

/**
 * Resolves a keydown to the Deckorate view it should open, or null when nothing
 * matches — including while focus sits in an editable control, so we never
 * intercept the user's typing.
 */
export function resolveShortcut(event: KeyboardEvent): DeckorateNav | null {
  if (isEditableTarget(event.target)) return null;
  const hit = NAV_SHORTCUTS.find((entry) =>
    matchesShortcut(event, entry.shortcut),
  );
  return hit ? hit.nav : null;
}
